'use client';

import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SingleValue } from 'react-select';
import Select from 'react-select';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { SelectorStyles, selectorLabel, selectorWrapper } from '../styles';
import type { SelectorOption, SelectorSaveOnBlurProps } from '../types';
import { SelectorOptionTypes } from '../types';
import { getOptionsComponent } from '../utils/getOptionsComponent';
import { triggerBlur } from '../utils/triggerBlur';
import ChevronDown from '/public/icons/chevron-down.svg';
import { getOptions } from '../utils/getOptions';

export const SelectorSaveOnBlur = <Value extends string | number>({
  className,
  label,
  options,
  intent = 'default',
  optionType = SelectorOptionTypes.DEFAULT,
  value,
  onChange,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  setSearchValue,
  customStyles,
  invalid,
  errors,
  name,
  disabled,
  required,
  helperText,
  ...rest
}: SelectorSaveOnBlurProps<Value>) => {
  const { register } = useFormContext();
  const errorMessage = getErrorMessage(errors, name ?? '');

  const onMenuScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onInputChange = (val: string) => {
    setSearchValue?.(val);
  };

  const onSelect = (newValue: SingleValue<SelectorOption<Value>>) => {
    onChange(newValue);
    triggerBlur(name ?? '');
  };

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);
  const isOldVersion = intent === 'old';

  return (
    <div className={selectorWrapper({ className })}>
      {label && <p className={selectorLabel({ intent, required })}>{label}</p>}
      <Select<SelectorOption<Value>>
        options={getOptions(options, selectedOption)}
        value={selectedOption}  
        required={required}
        onChange={onSelect}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onInputChange={onInputChange}
        components={{
          DropdownIndicator: (props) => (
            <ChevronDown
              className={`transition-transform duration-200 text-black fill-current w-4 h-4 ${props.selectProps.menuIsOpen ? 'rotate-180' : ''}`}
            />
          ),
          Option: (props) => getOptionsComponent(optionType)({ ...props, intent }),
          NoOptionsMessage: () => <p className="text-body-regular text-grey-700 text-center py-1">No options</p>,
        }}
        styles={SelectorStyles({
          invalid: Boolean(errorMessage) || invalid,
          customStyles,
          isOldVersion,
        })}
        {...rest}
        isDisabled={disabled}
      />
      {helperText && <p className="text-blue-300 text-body-small">{helperText}</p>}
      <input type="hidden" {...register(name ?? '')} />
    </div>
  );
};
